import type { ReportSectionRow, TeacherAssignment } from '../../../api/types';
import { PanelCard, Track } from '../../../components/ui';

/** Section teacher for this exam: the subject teacher of that section, else its homeroom teacher. */
export function sectionTeacher(
  assignments: TeacherAssignment[], gradeId: number | null, subjectId: number | null, sectionId: number,
): string | null {
  const here = assignments.filter((a) => a.school_grade === gradeId && (a.section == null || a.section.id === sectionId));
  const pick = here.find((a) => subjectId != null && a.subject === subjectId && a.section?.id === sectionId)
    ?? here.find((a) => subjectId != null && a.subject === subjectId)
    ?? here.find((a) => a.is_homeroom && a.section?.id === sectionId);
  return pick ? (pick.teacher_name || pick.teacher_username) : null;
}

/**
 * The footnote under the cards, from the section figures: how far the weakest section sits below
 * the grade average and how many of its students have yet to sit the exam. Null when not computable.
 */
export function sectionsFootnote(sections: ReportSectionRow[], gradeAvg: number | null): string | null {
  const withAvg = sections.filter((s) => s.avg_score != null);
  if (gradeAvg == null || withAvg.length === 0) return null;
  const worst = withAvg.reduce((a, b) => ((b.avg_score as number) < (a.avg_score as number) ? b : a));
  const gap = gradeAvg - (worst.avg_score as number);
  if (gap <= 0) return null;
  const yet = worst.assigned - worst.took;
  return `${worst.display || worst.name} is ${gap.toFixed(1)} pts below the grade${yet > 0 ? ` and has ${yet} student${yet === 1 ? '' : 's'} yet to sit the exam` : ''}.`;
}

/** Design `.sbtn` cards: one per section, click to filter the whole report to it (click again to clear). */
export default function SectionsComparedCard({ sections, selected, onSelect, gradeAvg, teacherOf }: {
  sections: ReportSectionRow[]; selected: number | 'all'; onSelect: (s: number | 'all') => void;
  gradeAvg: number | null; teacherOf: (sectionId: number) => string | null;
}) {
  const note = sectionsFootnote(sections, gradeAvg);
  return (
    <PanelCard aria-label="Sections compared" title="Sections compared" actions={<span className="text-[13px] text-muted">avg · pass</span>}>
      {sections.map((s) => {
        const on = selected === s.id;
        const pass = s.pass_rate == null ? null : s.pass_rate * 100;
        const teacher = teacherOf(s.id);
        const label = s.name || s.display;
        return (
          <button
            key={s.id}
            type="button"
            aria-pressed={on}
            onClick={() => onSelect(on ? 'all' : s.id)}
            className={`mb-2.5 flex w-full items-center gap-3 rounded-[14px] border bg-surface p-3 text-left text-ink transition-colors hover:border-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${on ? 'border-navy ring-1 ring-inset ring-navy' : 'border-line'}`}
          >
            <span aria-hidden="true" className="inline-grid h-9 w-9 flex-none place-items-center rounded-[12px] bg-mist text-xs font-semibold text-ink">{label}</span>
            <span className="sr-only">Section {label}</span>
            <span className="min-w-0 flex-1">
              <span className="mb-1.5 flex justify-between text-[13px]">
                <span className="font-semibold">{s.avg_score == null ? '—' : `${s.avg_score.toFixed(1)}%`} average</span>
                <span className="text-muted">{s.took} took</span>
              </span>
              <Track value={pass ?? 0} tone={pass != null && pass < 60 ? 'accent' : 'slate'} label={`${label} pass rate`} />
              <span className="mt-1 block text-xs text-muted">
                {pass == null ? 'No results yet' : `${pass.toFixed(1)}% passed`}{teacher ? ` · ${teacher}` : ''}
              </span>
            </span>
          </button>
        );
      })}
      {note && <p className="mt-1 text-xs text-muted">{note}</p>}
    </PanelCard>
  );
}
